import { useEffect, useId, useRef } from "react";

import { cn } from "@/lib/utils";
import {
  buildConnectorPath,
  getConnectorOrientation,
  type ConnectorOrientation,
} from "@/components/data-product-graph/geometry";
import {
  createParticleLoopState,
  getParticleFrameState,
  type ParticleLoopState,
} from "@/components/data-product-graph/particles";
import { sceneConfig } from "@/components/data-product-graph/scene-config";
import styles from "@/components/DataProductGraph.module.css";

interface DataProductGraphProps {
  className?: string;
}

interface RelativeBox {
  top: number;
  right: number;
  bottom: number;
  left: number;
  midX: number;
  midY: number;
}

interface EdgeDefinition {
  orientation: ConnectorOrientation;
  phaseOffset: number;
  period: number;
  start: {
    x: number;
    y: number;
  };
  end: {
    x: number;
    y: number;
  };
}

interface AnimatedEdge {
  path: SVGPathElement;
  phaseOffset: number;
  period: number;
}

interface ActiveParticle {
  circle: SVGCircleElement;
  path: SVGPathElement;
  state: ParticleLoopState;
}

const NODE_IDS = sceneConfig.cards.map((c) => c.id);

const { particlesPerEdge: PARTICLES_PER_EDGE } = sceneConfig.motion;

const createSvgElement = <T extends keyof SVGElementTagNameMap>(tag: T) =>
  document.createElementNS("http://www.w3.org/2000/svg", tag);

/**
 * Get node position in the local (pre-transform) coordinate space
 * of a container, using offset properties instead of getBoundingClientRect.
 * This ensures connectors draw correctly inside 3D-transformed containers.
 */
const getLocalBox = (container: HTMLElement, node: HTMLElement): RelativeBox => {
  let left = 0;
  let top = 0;
  let el: HTMLElement | null = node;

  // Walk up the offset parent chain until we reach the container
  while (el && el !== container) {
    left += el.offsetLeft;
    top += el.offsetTop;
    el = el.offsetParent as HTMLElement | null;
  }

  const width = node.offsetWidth;
  const height = node.offsetHeight;

  return {
    top,
    right: left + width,
    bottom: top + height,
    left,
    midX: left + width / 2,
    midY: top + height / 2,
  };
};

const clearElement = (element: SVGElement | null) => {
  if (!element) {
    return;
  }

  while (element.firstChild) {
    element.removeChild(element.firstChild);
  }
};

/**
 * Vertical spread offsets for edges converging on the same target node.
 * Maps edge-count → per-index y-offsets (horizontal) or x-offsets (vertical).
 */
const SPREAD_H: Record<number, number[]> = { 1: [0], 2: [-14, 14] };
const SPREAD_V: Record<number, number[]> = { 1: [0], 2: [-18, 18] };

const computeEdgeDefinitions = (
  orientation: ConnectorOrientation,
  nodeBoxes: Record<string, RelativeBox>,
): EdgeDefinition[] => {
  // Pre-compute how many edges converge on each target, and each edge's index
  const targetCounts: Record<string, number> = {};
  const edgeTargetIndex = new Map<string, number>();

  for (const edge of sceneConfig.edges) {
    const count = targetCounts[edge.to] || 0;
    edgeTargetIndex.set(edge.id, count);
    targetCounts[edge.to] = count + 1;
  }

  return sceneConfig.edges
    .map((edge) => {
      const fromBox = nodeBoxes[edge.from];
      const toBox = nodeBoxes[edge.to];
      if (!fromBox || !toBox) return null;

      const count = targetCounts[edge.to];
      const idx = edgeTargetIndex.get(edge.id) ?? 0;

      if (orientation === "horizontal") {
        const ySpread = SPREAD_H[count]?.[idx] ?? 0;
        return {
          orientation,
          phaseOffset: edge.timing.phaseOffset,
          period: edge.timing.period,
          start: { x: fromBox.right, y: fromBox.midY },
          end: { x: toBox.left - 10, y: toBox.midY + ySpread },
        };
      }

      const xSpread = SPREAD_V[count]?.[idx] ?? 0;
      return {
        orientation,
        phaseOffset: edge.timing.phaseOffset,
        period: edge.timing.period,
        start: { x: fromBox.midX, y: fromBox.bottom },
        end: { x: toBox.midX + xSpread, y: toBox.top - 2 },
      };
    })
    .filter((e): e is EdgeDefinition => e !== null);
};

export const DataProductGraph = ({ className }: DataProductGraphProps) => {
  const sceneRef = useRef<HTMLDivElement>(null);
  const graphPlaneRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const svgRef = useRef<SVGSVGElement>(null);
  const edgesLayerRef = useRef<SVGGElement>(null);
  const particlesLayerRef = useRef<SVGGElement>(null);
  const markerId = `lumient-arrowhead-${useId().replace(/:/g, "")}`;

  useEffect(() => {
    const scene = sceneRef.current;
    const graphPlane = graphPlaneRef.current;
    const svg = svgRef.current;
    const edgesLayer = edgesLayerRef.current;
    const particlesLayer = particlesLayerRef.current;

    if (!scene || !graphPlane || !svg || !edgesLayer || !particlesLayer) {
      return;
    }

    let animationFrameId = 0;
    let resizeTimeout = 0;
    let sequenceVersion = 0;
    let resizeObserver: ResizeObserver | null = null;
    const timeouts: number[] = [];

    // Track per-node event listeners so we can clean them up on reset
    const nodeAnimEndHandlers = new Map<HTMLElement, EventListener>();
    const nodeHoverHandlers = new Map<HTMLElement, { enter: EventListener; leave: EventListener }>();

    const clearTimers = () => {
      while (timeouts.length) {
        const timeout = timeouts.pop();

        if (timeout) {
          window.clearTimeout(timeout);
        }
      }
    };

    const stopParticles = () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
        animationFrameId = 0;
      }

      clearElement(particlesLayer);
    };

    const resetNodes = () => {
      for (const id of NODE_IDS) {
        const node = nodeRefs.current[id];
        if (!node) continue;

        // Cancel any pending animationend handler
        const animEndHandler = nodeAnimEndHandlers.get(node);
        if (animEndHandler) {
          node.removeEventListener("animationend", animEndHandler);
          nodeAnimEndHandlers.delete(node);
        }

        // Remove hover handlers
        const hoverHandlers = nodeHoverHandlers.get(node);
        if (hoverHandlers) {
          node.removeEventListener("mouseenter", hoverHandlers.enter);
          node.removeEventListener("mouseleave", hoverHandlers.leave);
          nodeHoverHandlers.delete(node);
        }

        node.classList.remove(styles.materialized);
        node.classList.remove(styles.composed);
        node.classList.remove(styles.hovered);
        node.style.opacity = "";
        node.style.transform = "";
      }
    };

    const drawEdges = (): AnimatedEdge[] => {
      // Use local (pre-transform) dimensions for SVG viewBox
      const planeW = graphPlane.offsetWidth;
      const planeH = graphPlane.offsetHeight;

      svg.setAttribute("viewBox", `0 0 ${planeW || 1} ${planeH || 1}`);
      clearElement(edgesLayer);
      stopParticles();

      if (!planeW || !planeH) {
        return [];
      }

      const orientation: ConnectorOrientation = getConnectorOrientation(window.innerWidth);

      // Build box map from all node refs
      const boxes: Record<string, RelativeBox> = {};
      for (const id of NODE_IDS) {
        const el = nodeRefs.current[id];
        if (el) boxes[id] = getLocalBox(graphPlane, el);
      }

      const edgeDefs = computeEdgeDefinitions(orientation, boxes);

      return edgeDefs.map(({ orientation: edgeOrientation, phaseOffset, period, start, end }) => {
        const path = createSvgElement("path");
        path.setAttribute("d", buildConnectorPath({ orientation: edgeOrientation, start, end }));
        path.setAttribute("class", styles.edgePath);
        path.setAttribute("marker-end", `url(#${markerId})`);
        edgesLayer.appendChild(path);

        return { path, phaseOffset, period };
      });
    };

    const startParticles = (edges: AnimatedEdge[]) => {
      const particles: ActiveParticle[] = [];

      edges.forEach(({ path, phaseOffset, period }) => {
        if (typeof path.getTotalLength !== "function" || typeof path.getPointAtLength !== "function") {
          return;
        }

        const length = path.getTotalLength();

        if (!length) {
          return;
        }

        const spread = period / PARTICLES_PER_EDGE;

        for (let index = 0; index < PARTICLES_PER_EDGE; index += 1) {
          const circle = createSvgElement("circle");
          circle.setAttribute("class", styles.particle);
          circle.setAttribute("r", "4");
          circle.setAttribute("data-r", "4");
          circle.setAttribute("fill", "oklch(var(--primary))");
          circle.setAttribute("opacity", "0");
          particlesLayer.appendChild(circle);

          particles.push({
            circle,
            path,
            state: createParticleLoopState({
              length,
              period,
              phaseOffset: phaseOffset + index * spread,
            }),
          });
        }
      });

      if (!particles.length) {
        return;
      }

      const animationStartTime = performance.now();

      const frame = (now: number) => {
        const elapsedMs = now - animationStartTime;

        particles.forEach((particle) => {
          const renderState = getParticleFrameState(particle.state, elapsedMs);
          const point = particle.path.getPointAtLength(renderState.distance);

          particle.circle.setAttribute("cx", point.x.toFixed(2));
          particle.circle.setAttribute("cy", point.y.toFixed(2));
          particle.circle.setAttribute("r", renderState.radius.toFixed(2));
          particle.circle.setAttribute("opacity", renderState.opacity.toFixed(3));
        });

        animationFrameId = window.requestAnimationFrame(frame);
      };

      animationFrameId = window.requestAnimationFrame(frame);
    };

    const schedule = (version: number, callback: () => void, delay: number) => {
      const timeout = window.setTimeout(() => {
        if (version !== sequenceVersion) {
          return;
        }

        callback();
      }, delay);

      timeouts.push(timeout);
    };

    const runSequence = () => {
      sequenceVersion += 1;
      const currentVersion = sequenceVersion;

      clearTimers();
      clearElement(edgesLayer);
      stopParticles();
      resetNodes();

      const {
        nodeDelays,
        composedNodeId,
        edgeDrawDelay,
        edgeVisibilityDelay,
        particleStartDelay,
        composedGlowDelay,
      } = sceneConfig.motion;

      // Materialize each node at its configured delay, then transfer
      // transform ownership from the CSS animation to inline style so that
      // CSS hover transitions can work (animations override CSS :hover rules).
      for (const [id, delay] of Object.entries(nodeDelays)) {
        schedule(currentVersion, () => {
          const node = nodeRefs.current[id];
          if (!node) return;

          node.classList.add(styles.materialized);

          const handleAnimEnd: EventListener = () => {
            // Guard: reset may have already cleared this node
            if (!node.classList.contains(styles.materialized)) return;

            // Hand off transform to inline style — same final value as keyframe 100%
            node.style.opacity = "1";
            node.style.transform = "translateY(0) translateZ(12px) scale(1)";
            node.classList.remove(styles.materialized);
            nodeAnimEndHandlers.delete(node);

            // Now CSS transitions work — wire up the hover lift
            const enter: EventListener = () => {
              node.style.transform = "translateY(-4px) translateZ(22px) scale(1)";
              node.classList.add(styles.hovered);
            };
            const leave: EventListener = () => {
              node.style.transform = "translateY(0) translateZ(12px) scale(1)";
              node.classList.remove(styles.hovered);
            };

            nodeHoverHandlers.set(node, { enter, leave });
            node.addEventListener("mouseenter", enter);
            node.addEventListener("mouseleave", leave);
          };

          nodeAnimEndHandlers.set(node, handleAnimEnd);
          node.addEventListener("animationend", handleAnimEnd);
        }, delay);
      }

      // Draw edges, then reveal particles and composed glow
      schedule(currentVersion, () => {
        const edges = drawEdges();

        schedule(currentVersion, () => {
          edges.forEach(({ path }) => path.classList.add(styles.visible));
        }, edgeVisibilityDelay);

        schedule(currentVersion, () => {
          startParticles(edges);
        }, edgeVisibilityDelay + particleStartDelay);

        schedule(currentVersion, () => {
          nodeRefs.current[composedNodeId]?.classList.add(styles.composed);
        }, edgeVisibilityDelay + composedGlowDelay);
      }, edgeDrawDelay);
    };

    const handleResize = () => {
      window.clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(runSequence, 120);
    };

    runSequence();

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(scene);
    } else {
      window.addEventListener("resize", handleResize);
    }

    return () => {
      sequenceVersion += 1;
      window.clearTimeout(resizeTimeout);
      clearTimers();
      stopParticles();
      clearElement(edgesLayer);
      resetNodes();

      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", handleResize);
      }
    };
  }, [markerId]);

  return (
    <div
      ref={sceneRef}
      className={cn(styles.scene, className)}
      data-testid="data-product-graph"
    >
      <div ref={graphPlaneRef} className={styles.graphPlane}>
        <div className={styles.graph}>
          {/* ── Stage 1: Sources ── */}

          <div
            ref={(el) => { nodeRefs.current["invoices"] = el; }}
            className={cn(styles.node, styles.nodeInvoices)}
          >
            <div className={styles.nodeHeader}>
              <div className={styles.nodeIcon}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              </div>
              <div className={styles.nodeName}>Invoices</div>
              <span className={cn(styles.tag, styles.tagSource, styles.tagTopRight)}>PDF</span>
            </div>
            <div className={styles.divider} />
            <div className={styles.meta}>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Updated</span>
                <span className={styles.metaValueSubtle}>2h ago</span>
              </div>
            </div>
          </div>

          <div
            ref={(el) => { nodeRefs.current["payments"] = el; }}
            className={cn(styles.node, styles.nodePayments)}
          >
            <div className={styles.nodeHeader}>
              <div className={styles.nodeIcon}>
                <img src="/brand/stripe-icon.svg" alt="" aria-hidden="true" className={styles.nodeIconImage} />
              </div>
              <div className={styles.nodeName}>Payments</div>
              <span className={cn(styles.tag, styles.tagSource, styles.tagTopRight)}>API</span>
            </div>
            <div className={styles.divider} />
            <div className={styles.meta}>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Updated</span>
                <span className={styles.metaValueSubtle}>15m ago</span>
              </div>
            </div>
          </div>

          <div
            ref={(el) => { nodeRefs.current["contract-terms"] = el; }}
            className={cn(styles.node, styles.nodeContractTerms)}
          >
            <div className={styles.nodeHeader}>
              <div className={styles.nodeIcon}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                  <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                </svg>
              </div>
              <div className={styles.nodeName}>Contract Terms</div>
              <span className={cn(styles.tag, styles.tagSource, styles.tagTopRight)}>PDF</span>
            </div>
            <div className={styles.divider} />
            <div className={styles.meta}>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Updated</span>
                <span className={styles.metaValueSubtle}>Today</span>
              </div>
            </div>
          </div>

          {/* ── Stage 2: Intermediates ── */}

          <div
            ref={(el) => { nodeRefs.current["payables-status"] = el; }}
            className={cn(styles.node, styles.composedNode, styles.nodePayablesStatus)}
          >
            <div className={styles.nodeHeader}>
              <div className={styles.nodeIcon}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <polygon points="12 2 2 7 12 12 22 7 12 2" />
                  <polyline points="2 17 12 22 22 17" />
                  <polyline points="2 12 12 17 22 12" />
                </svg>
              </div>
              <div className={styles.nodeName}>Payables Status</div>
            </div>
            <div className={styles.divider} />
            <div className={styles.meta}>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Open</span>
                <span className={cn(styles.metaValue, styles.metaValueHi)}>12</span>
              </div>
              <div className={styles.metaRow}>
                <span className={cn(styles.metaLabel, styles.danger)}>Overdue</span>
                <span className={cn(styles.metaValue, styles.danger)}>3</span>
              </div>
            </div>
          </div>

          <div
            ref={(el) => { nodeRefs.current["contract-status"] = el; }}
            className={cn(styles.node, styles.composedNode, styles.nodeContractStatus)}
          >
            <div className={styles.nodeHeader}>
              <div className={styles.nodeIcon}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <div className={styles.nodeName}>Contract Status</div>
            </div>
            <div className={styles.divider} />
            <div className={styles.meta}>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Active</span>
                <span className={cn(styles.metaValue, styles.metaValueHi)}>8</span>
              </div>
              <div className={styles.metaRow}>
                <span className={cn(styles.metaLabel, styles.danger)}>Expiring</span>
                <span className={cn(styles.metaValue, styles.danger)}>2</span>
              </div>
            </div>
          </div>

          {/* ── Stage 3: Final Composed Record ── */}

          <div
            ref={(el) => { nodeRefs.current["supplier-risk"] = el; }}
            className={cn(styles.node, styles.composedNode, styles.nodeSupplierRisk)}
          >
            <div className={styles.nodeHeader}>
              <div className={styles.nodeIcon}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <polygon points="12 2 2 7 12 12 22 7 12 2" />
                  <polyline points="2 17 12 22 22 17" />
                  <polyline points="2 12 12 17 22 12" />
                </svg>
              </div>
              <div className={styles.nodeName}>Supplier Risk</div>
            </div>
            <div className={styles.divider} />
            <div className={styles.meta}>
              <div className={cn(styles.metaRow, styles.metaRowDanger)}>
                <span className={cn(styles.metaLabel, styles.danger)}>High Risk</span>
                <span className={cn(styles.metaValue, styles.danger)}>
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  2
                </span>
              </div>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Under Review</span>
                <span className={styles.metaValue}>4</span>
              </div>
            </div>
          </div>
        </div>

        <svg ref={svgRef} className={styles.edgeCanvas} data-testid="edge-canvas" aria-hidden="true">
          <defs>
            <marker id={markerId} markerWidth="6" markerHeight="5" refX="5" refY="2.5" orient="auto">
              <polygon points="0 0, 6 2.5, 0 5" fill="oklch(var(--primary))" />
            </marker>
          </defs>
          <g ref={edgesLayerRef} />
          <g ref={particlesLayerRef} />
        </svg>
      </div>
    </div>
  );
};

export default DataProductGraph;
